import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { cachedAxiosGet } from '../utils/apiCache';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { AreaChart, BarChart, Area, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { getBackendUrl } from '../config';
import { normalizeDisplayPowerUnit, powerEnergyLabels, chartPowerTickLabel } from '../utils/displayPowerUnit';

const BACKEND_URL = getBackendUrl();
/** Cache TTL for chart interval refetch (matches prior inline constant). */
const CHART_CACHE_TTL_MS = 2 * 60 * 1000;

const dlTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function clampDlChartDate(ymd, period) {
  const t = dlTodayStr();
  if (!ymd || typeof ymd !== 'string') return t;
  if (period === 'Day' || period === 'Week') return ymd > t ? t : ymd;
  if (period === 'Month') {
    const tm = t.slice(0, 7);
    return ymd.slice(0, 7) > tm ? `${tm}-01` : ymd;
  }
  if (period === 'Year') {
    const y = Number(ymd.slice(0, 4));
    const ty = new Date().getFullYear();
    return y > ty ? `${ty}-01-01` : ymd;
  }
  return ymd;
}

// Siguron që .toFixed të mos thirret kur vlera mungon ose nuk është numër
const safeToFixed = (v, decimals = 2) =>
  v != null && Number.isFinite(Number(v)) ? Number(v).toFixed(decimals) : '0.00';

/** Shuma e “Fuqia (kW)” nga inverterët e këtij logger-i → MW në UI = vlera / 1000 */
function sumInverterRatedPowerKw(inverterList) {
  if (!Array.isArray(inverterList)) return 0;
  return inverterList.reduce((sum, inv) => {
    const raw =
      inv?.power_kw ??
      inv?.rated_power_kw ??
      inv?.powerKw ??
      inv?.ratedPowerKw;
    const n = Number(raw);
    return sum + (Number.isFinite(n) ? Math.max(0, n) : 0);
  }, 0);
}

// Format alarm for display: "License Expired: OFF" – name readable (snake_case → Title Case), state ON/OFF
const formatAlarmDisplay = (a) => {
  const raw = (a?.name ?? a?.message ?? a?.description ?? '').trim();
  const name = raw
    ? raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Alarm';
  const state = (a?.state ?? 'OFF').toUpperCase();
  return `${name}: ${state}`;
};

// ============================================
// MOBILE VIEW COMPONENT
// ============================================
const MobileDataLoggerView = ({ dataLogger, company, companiesList = [], inverters, inverterStatusCounts = {}, safeReadings: safeReadingsProp, meterSections: meterSectionsProp, navigate, isDark, readings = [], alarms = [], alarmHistory = [], lastUpdated = null, fetchData, powerDisplayUnit = 'mw', t = (k) => k }) => {
  const datePickerRef = useRef(null);
  const monthPickerRef = useRef(null);
  const [mainTab, setMainTab] = useState('details'); // details | realtime | alarms (si Energy Meter)
  const [graphPeriod, setGraphPeriod] = useState('Day');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [expandedDetail, setExpandedDetail] = useState(null);

  const latestReading = readings.length > 0 ? readings[0] : null;
  const labels = powerEnergyLabels(powerDisplayUnit);
  const safeReadings = safeReadingsProp ?? buildDataLoggerReadings(latestReading?.payload, powerDisplayUnit);
  const detailSections = meterSectionsProp ?? buildMeterSectionsWithPayload(latestReading?.payload, powerDisplayUnit);

  const onlineCount = inverters.filter(inv => inv.status === 'ONLINE').length;
  const faultCount = inverters.filter(inv => inv.status === 'FAULT').length;
  const offlineCount = inverters.filter(inv => inv.status === 'OFFLINE').length;
  const standbyCount = (inverterStatusCounts['Standby'] || 0) + (inverterStatusCounts['Idle'] || 0);

  const cardShadowClass = "shadow-[6px_6px_12px_rgba(0,0,0,0.08),-6px_-6px_12px_rgba(255,255,255,0.9)] dark:shadow-none";

  const [mobileSelectedDate, setMobileSelectedDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const graphData = buildDataLoggerChartData(readings, mobileSelectedDate, graphPeriod === 'Lifetime' ? 'Total' : graphPeriod);
  const chartDataKey = graphPeriod === 'Day' ? 'time' : 'name';

  const changeMobileChartDate = (delta) => {
    setMobileSelectedDate((prev) => {
      const base = prev || dlTodayStr();
      const d = new Date(`${base}T12:00:00`);
      if (Number.isNaN(d.getTime())) return dlTodayStr();
      if (graphPeriod === 'Day') d.setDate(d.getDate() + delta);
      else if (graphPeriod === 'Month') d.setMonth(d.getMonth() + delta);
      else if (graphPeriod === 'Year') d.setFullYear(d.getFullYear() + delta);
      return clampDlChartDate(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
        graphPeriod,
      );
    });
  };

  const openMobileDlPeriodPicker = () => {
    if (graphPeriod === 'Day') {
      datePickerRef.current?.showPicker?.();
      datePickerRef.current?.click();
    } else if (graphPeriod === 'Month') {
      monthPickerRef.current?.showPicker?.();
      monthPickerRef.current?.click();
    }
  };

  const md = new Date(mobileSelectedDate);
  const mobileGraphDateDisplay =
    graphPeriod === 'Day'
      ? mobileSelectedDate
      : graphPeriod === 'Month'
        ? `${String(md.getMonth() + 1).padStart(2, '0')}.${md.getFullYear()}`
        : graphPeriod === 'Year'
          ? String(md.getFullYear())
          : mobileSelectedDate;

  // Main Accessories Page
  return (
    <Layout>
      <div className="h-full flex flex-col bg-[#f5f5f5] dark:bg-gradient-to-b dark:from-[#0f172a] dark:to-black">
        {/* Header with Company Dropdown */}
        <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-[#0f172a]">
          <div className="flex items-center justify-between mb-3">
            <div className="relative">
              <button
                onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                className="flex items-center gap-2"
              >
                <span className="text-lg font-bold text-gray-900 dark:text-white">{company?.name || t('noCompanies')}</span>
                <svg className={`w-4 h-4 text-gray-500 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showCompanyDropdown && companiesList.length > 0 && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-transparent">
                  {companiesList.map(c => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setShowCompanyDropdown(false);
                        navigate(`/devices?company=${c.id}`);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition border-b border-gray-100 dark:border-[#2d3a4f] last:border-0 ${company?.id === c.id
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-medium'
                        : 'text-gray-700 dark:text-white'
                        }`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => company ? navigate(`/devices?company=${company.id}`) : navigate('/devices')}
              className="w-10 h-10 bg-[#e8e8e8] dark:bg-slate-700 rounded-full flex items-center justify-center"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs – mobile +20% më të mëdhenj për tap më të lehtë */}
          <div className="flex gap-2 flex-1 min-w-0">
            <button
              onClick={() => company?.id ? navigate(`/devices?company=${company.id}`) : navigate('/devices')}
              className="px-3 py-2 rounded-full text-sm font-medium transition shrink-0 bg-blue-600 text-white"
            >
              {t('allDevices')}
            </button>
            <button
              onClick={() => company?.id ? navigate(`/devices?company=${company.id}&tab=inverter`) : navigate('/devices?tab=inverter')}
              className="px-3 py-2 rounded-full text-sm font-medium transition shrink-0 bg-[#e8e8e8] dark:bg-slate-700 text-gray-600 dark:text-gray-300"
            >
              {t('inverterTab')}
            </button>
            <button
              onClick={() => company?.id ? navigate(`/devices?company=${company.id}&tab=ems`) : navigate('/devices?tab=ems')}
              className="px-3 py-2 rounded-full text-sm font-medium transition shrink-0 bg-[#e8e8e8] dark:bg-slate-700 text-gray-600 dark:text-gray-300"
            >
              {t('emsTab')}
            </button>
          </div>
        </div>

        {/* Section Title */}
        <div className="px-4 py-3 bg-[#f0f0f0] dark:bg-[#0f172a]/80">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('dataLogger')}</h3>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {/* Online Badge + Device Image */}
          <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 mt-3 ${cardShadowClass}`}>
            <div className="flex items-start justify-between mb-3">
              <span className="px-3 py-1 bg-[#22c55e] text-white text-xs font-bold rounded">{t('online')}</span>
              <span className="text-gray-900 dark:text-white font-bold text-lg">
                {normalizeDisplayPowerUnit(powerDisplayUnit) === 'kw'
                  ? `${safeToFixed(sumInverterRatedPowerKw(inverters), 2)} kW`
                  : `${safeToFixed(sumInverterRatedPowerKw(inverters) / 1000, 2)} MW`}
              </span>
            </div>
            <div className="h-28 flex items-center justify-center mb-3">
              {dataLogger?.photo_url ? (
                <img
                  src={`${BACKEND_URL}${dataLogger.photo_url}`}
                  alt={dataLogger.name}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <svg className="w-20 h-20 text-gray-300 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              )}
            </div>
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 text-sm">{t('dataLogger')} • {dataLogger?.name || dataLogger?.brand || dataLogger?.serial_number || '—'} ›</p>
              {lastUpdated && <p className="text-gray-400 dark:text-gray-500 text-xs">{t('updatedLabel')} {lastUpdated}</p>}
            </div>
          </div>

          {/* Tabs: Details | Real Time | Alarms – menjëherë mbas fotos (si Energy Meter) */}
          <div className="flex border-b border-gray-200 dark:border-slate-700 mt-4 gap-1">
            <button
              onClick={() => setMainTab('details')}
              className={`px-3 py-3 text-sm font-medium flex-1 ${mainTab === 'details' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`}
            >
              {t('details')}
            </button>
            <button
              onClick={() => setMainTab('realtime')}
              className={`px-3 py-3 text-sm font-medium flex-1 ${mainTab === 'realtime' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`}
            >
              {t('realTime')}
            </button>
            <button
              onClick={() => setMainTab('alarms')}
              className={`px-3 py-3 text-sm font-medium flex-1 flex items-center justify-center gap-1 ${mainTab === 'alarms' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`}
            >
              {t('alarms')} <span className={`text-xs px-1.5 py-0.5 rounded ${(alarms?.filter((a) => String(a?.state ?? 'OFF').toUpperCase() === 'ON').length || 0) > 0 ? 'bg-red-500/80 text-white' : 'bg-gray-300 dark:bg-slate-600 text-gray-600 dark:text-gray-400'}`}>{alarms?.filter((a) => String(a?.state ?? 'OFF').toUpperCase() === 'ON').length ?? 0}</span>
            </button>
          </div>

          {mainTab === 'alarms' ? (
            /* Alarms – lista + histori */
            <div className={`mt-4 space-y-2 ${cardShadowClass}`}>
              <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1e293b] border border-slate-700' : 'bg-white border border-gray-200'}`}>
                <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-600">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{t('alarms')} ({alarms?.length ?? 0})</h3>
                </div>
                <div className={`px-4 pb-3 max-h-40 overflow-y-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {(alarms?.length || 0) === 0 ? (
                    <p className="text-sm py-4">{t('noAlarms')}</p>
                  ) : (
                    alarms.map((a, i) => {
                      const state = (a?.state ?? 'OFF').toUpperCase();
                      const isOn = state === 'ON';
                      return (
                        <div key={a?.id || a?.no || i} className={`py-2.5 border-b border-gray-200 dark:border-slate-600 last:border-0 ${isOn ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                          <span className={`text-sm ${isOn ? 'text-red-700 dark:text-red-300 font-medium' : 'text-gray-900 dark:text-white'}`}>{formatAlarmDisplay(a)}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              {alarmHistory.length > 0 && (
                <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1e293b] border border-slate-700' : 'bg-white border border-gray-200'}`}>
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-slate-600">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('alarmHistory') || 'Histori alarmeve'}</h3>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className={isDark ? 'bg-slate-800/50 text-gray-400' : 'bg-gray-50 text-gray-600'}>
                        <tr><th className="px-2 py-1.5 text-left font-medium">Data/ora</th><th className="px-2 py-1.5 text-left font-medium">Status</th></tr>
                      </thead>
                      <tbody className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                        {alarmHistory.slice(0, 30).map((h, i) => (
                          <tr key={i} className={i % 2 === 0 ? (isDark ? 'bg-[#1e293b]' : 'bg-white') : (isDark ? 'bg-slate-800/30' : 'bg-gray-50/50')}>
                            <td className="px-2 py-1 whitespace-nowrap">{h.ts ? new Date(h.ts).toLocaleString('sq-AL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                            <td className={`px-2 py-1 font-medium ${h.active ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>{h.active ? (t('alarmActive') || 'Aktiv') : (t('alarmInactive') || 'Jo aktiv')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : mainTab === 'realtime' ? (
            /* Real Time – grafiku shfaqet vetëm kur prek Real Time */
            <div className="mt-4 space-y-2">
              <div className={`rounded-xl overflow-hidden border border-gray-200 dark:border-transparent ${cardShadowClass}`}>
                <div className="bg-white dark:bg-[#1e293b] p-4">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-4">{t('energyAnalysis')}</h3>
                  <div className="flex gap-2 mb-4">
                    {['Day', 'Month', 'Year', 'Lifetime'].map((period) => (
                      <button
                        key={period}
                        onClick={() => setGraphPeriod(period)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium ${graphPeriod === period
                          ? 'bg-[#22c55e] text-white'
                          : 'bg-[#e8e8e8] dark:bg-slate-700 text-gray-600 dark:text-gray-300'
                          }`}
                      >
                        {period === 'Day' ? t('dayLabel') : period === 'Month' ? t('monthLabel') : period === 'Year' ? t('yearLabel') : t('lifetime')}
                      </button>
                    ))}
                  </div>
                  {(graphPeriod === 'Day' || graphPeriod === 'Month' || graphPeriod === 'Year') && (
                    <div className="flex items-center justify-center gap-4 mb-4">
                      <input
                        ref={datePickerRef}
                        type="date"
                        max={dlTodayStr()}
                        value={mobileSelectedDate || dlTodayStr()}
                        onChange={(e) => e.target.value && setMobileSelectedDate(clampDlChartDate(e.target.value, graphPeriod))}
                        className="sr-only"
                        tabIndex={-1}
                        aria-hidden
                      />
                      <input
                        ref={monthPickerRef}
                        type="month"
                        max={dlTodayStr().slice(0, 7)}
                        value={(mobileSelectedDate || dlTodayStr()).slice(0, 7)}
                        onChange={(e) => e.target.value && setMobileSelectedDate(clampDlChartDate(`${e.target.value}-01`, graphPeriod))}
                        className="sr-only"
                        tabIndex={-1}
                        aria-hidden
                      />
                      <button type="button" onClick={() => changeMobileChartDate(-1)} className="text-gray-600 dark:text-gray-400">
                        ◀
                      </button>
                      {graphPeriod === 'Year' ? (
                        <select
                          value={(mobileSelectedDate || dlTodayStr()).slice(0, 4)}
                          onChange={(e) => setMobileSelectedDate(clampDlChartDate(`${e.target.value}-01-01`, 'Year'))}
                          className="text-sm text-gray-800 dark:text-gray-100 bg-[#e8e8e8] dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 min-w-[5rem]"
                        >
                          {Array.from({ length: Math.max(1, new Date().getFullYear() - 2019) }, (_, i) => 2020 + i)
                            .filter((y) => y <= new Date().getFullYear())
                            .map((y) => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                      ) : (
                        <button
                          type="button"
                          onClick={openMobileDlPeriodPicker}
                          className="text-sm text-gray-700 dark:text-white min-w-[6.5rem] text-center"
                        >
                          {mobileGraphDateDisplay}
                        </button>
                      )}
                      <button type="button" onClick={() => changeMobileChartDate(1)} className="text-gray-600 dark:text-gray-400">
                        ▶
                      </button>
                    </div>
                  )}
                  <div className="bg-gray-50 dark:bg-[#1e293b] border-t border-gray-200 dark:border-slate-600/50 p-4">
                    <div className="h-44 min-h-[180px]">
                      <ResponsiveContainer width="100%" height="100%">
                        {graphPeriod === 'Day' ? (
                          <AreaChart data={graphData} margin={{ top: 14, right: 12, left: 8, bottom: 8 }}>
                            <defs><linearGradient id="dlFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22d3ee" stopOpacity={0.35} /><stop offset="100%" stopColor="#22d3ee" stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} vertical={false} />
                            <XAxis dataKey={chartDataKey} axisLine={false} tickLine={false} tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }} interval={0} ticks={['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:00']} />
                            <YAxis axisLine={false} tickLine={false} width={58} padding={{ top: 10, bottom: 8 }} tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }} tickFormatter={(v) => chartPowerTickLabel(v, powerDisplayUnit)} />
                            <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`, borderRadius: '8px', color: isDark ? '#fff' : '#000' }} labelFormatter={(label) => (label ? `Koha: ${label}` : label)} formatter={(v) => [chartPowerTickLabel(v, powerDisplayUnit), t('productionLabel')]} />
                            <Area type="linear" dataKey="value" fill="url(#dlFill)" stroke="none" />
                            <Line type="linear" dataKey="value" stroke="#22d3ee" strokeWidth={2} dot={false} />
                          </AreaChart>
                        ) : (
                          <BarChart data={graphData} margin={{ top: 14, right: 12, left: 8, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} vertical={false} />
                            <XAxis dataKey={chartDataKey} axisLine={false} tickLine={false} tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }} interval="preserveStartEnd" />
                            <YAxis axisLine={false} tickLine={false} width={58} padding={{ top: 10, bottom: 8 }} tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }} tickFormatter={(v) => chartPowerTickLabel(v, powerDisplayUnit)} />
                            <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`, borderRadius: '8px', color: isDark ? '#fff' : '#000' }} formatter={(v) => [chartPowerTickLabel(v, powerDisplayUnit), t('productionLabel')]} />
                            <Bar dataKey="value" fill="#22d3ee" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Details – metric cards + detail dropdowns */
            <>
              {/* 5 metric cards + Online/Offline status */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{safeToFixed(safeReadings.active_power, 2)} <span className="text-sm font-normal ml-1">{labels.power}</span></p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('activePower')}</p>
                </div>
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{safeToFixed(safeReadings.reactive_power, 2)} <span className="text-sm font-normal ml-1">{labels.reactive}</span></p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('reactivePower')}</p>
                </div>
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{safeReadings.power_factor}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('powerFactor')}</p>
                </div>
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{safeToFixed(safeReadings.energy_yield_of_current_day, 2)} <span className="text-sm font-normal ml-1">{labels.energy}</span></p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('energyToday')}</p>
                </div>
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{safeToFixed(safeReadings.total_energy_yield, 2)} <span className="text-sm font-normal ml-1">{labels.energy}</span></p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('totalEnergyYield')}</p>
                </div>
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{safeToFixed(Number(safeReadings.co2_kg || 0) / 1000, 2)} <span className="text-sm font-normal ml-1">T/CO2</span></p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('co2Label')}</p>
                </div>
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 col-span-2 flex items-center justify-between gap-2 flex-wrap ${cardShadowClass}`}>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-green-500 rounded-full shrink-0"></span><span className="text-sm font-medium text-gray-900 dark:text-white">{t('online')}</span></span>
                    <span className="text-lg font-bold">
                      {offlineCount === 0 ? (
                        <span className="text-green-500">{onlineCount}</span>
                      ) : (
                        <><span className="text-green-500">{onlineCount}</span><span className="text-gray-400 mx-0.5">/</span><span className="text-red-500">{offlineCount}</span></>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-red-500 rounded-full shrink-0"></span><span className="text-sm font-medium text-gray-900 dark:text-white">{t('fault')}</span></span>
                    <span className="text-lg font-bold text-red-500">{faultCount}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-gray-400 rounded-full shrink-0"></span><span className="text-sm font-medium text-gray-900 dark:text-white">{t('standby')}</span></span>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">{standbyCount}</span>
                  </div>
                </div>
              </div>

              {/* Details – dropdown */}
              <div className="mt-3 space-y-2">
                {detailSections.map((section) => (
                  <div key={section.name} className={`bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-transparent rounded-xl overflow-hidden ${cardShadowClass}`}>
                    <button
                      onClick={() => setExpandedDetail(expandedDetail === section.name ? null : section.name)}
                      className="w-full flex items-center justify-between p-4 text-left"
                    >
                      <span className="font-medium text-gray-900 dark:text-white">{t(DL_SECTION_KEYS[section.name] || section.name)}</span>
                      <svg className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform flex-shrink-0 ml-2 ${expandedDetail === section.name ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedDetail === section.name && (
                      <div className="px-4 pb-4 pt-0 border-t border-gray-200 dark:border-slate-600/50">
                        <div className="space-y-2 mt-3">
                          {section.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">{t(DL_LABEL_KEYS[item.label] || item.label)}</span>
                              <span className="text-gray-900 dark:text-white font-medium">{item.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

// Map për përkthim të seksioneve dhe etiketave Data Logger
// Vetëm seksionet monitoring (jo kontrolle – kontrollet janë vetëm në Control Datalogger)
const DL_SECTION_KEYS = {
  'Input Power / DC': 'dlInputPowerDc',
  'Grid Phase Currents': 'dlGridPhaseCurrents',
  'Lock Status': 'dlLockStatus',
  'KPI': 'dlKpi',
};
const DL_LABEL_KEYS = {
  'Active power': 'dlActivePower',
  'DC power': 'dlDcPower',
  'Input power (kW)': 'dlInputPowerKw',
  'DC current (A)': 'dlDcCurrentA',
  'Grid A phase current (A)': 'dlGridAPhaseCurrent',
  'Grid B phase current (A)': 'dlGridBPhaseCurrent',
  'Grid C phase current (A)': 'dlGridCPhaseCurrent',
  'Lock status': 'dlLockStatusLabel',
  'Reduced CO2 emission (kg)': 'dlReducedCo2Kg',
  'CO2 (T/CO2)': 'co2Tons',
};

// Seksionet e Data Logger – vetëm monitoring (pa kontrolle)
// Kontrollet (Reactive/Active Limits, PV/ESS Limits, API Override, Remote Scheduling) janë vetëm në Control Datalogger
const DATALOGGER_DETAIL_SECTIONS = [
  {
    name: 'Input Power / DC',
    items: [
      { label: 'Active power', keys: ['active_power_mw', 'total_active_power'], formatMwField: true },
      { label: 'DC power', keys: ['dc_power_mw', 'input_power_mw', 'input_power', 'pv_power', 'dc_power', 'pvPower', 'inputPower', 'dcPower'], formatMwField: true },
      { label: 'DC current (A)', keys: ['dc_current_a', 'dc_current', 'idc', 'pv_current', 'input_current', 'dcCurrent'] },
    ]
  },
  {
    name: 'Grid Phase Currents',
    items: [
      { label: 'Grid A phase current (A)', keys: ['grid_current_a_a', 'phase_current_ia', 'current_ia', 'ia', 'Ia', 'grid_current_a', 'grid_phase_a_current'] },
      { label: 'Grid B phase current (A)', keys: ['grid_current_b_a', 'phase_current_ib', 'current_ib', 'ib', 'Ib', 'grid_current_b', 'grid_phase_b_current'] },
      { label: 'Grid C phase current (A)', keys: ['grid_current_c_a', 'phase_current_ic', 'current_ic', 'ic', 'Ic', 'grid_current_c', 'grid_phase_c_current'] },
    ]
  },
  {
    name: 'Lock Status',
    items: [
      { label: 'Lock status', keys: ['lock_status', 'locked', 'unlock', 'lockStatus', 'locked_status'] },
    ]
  },
  {
    name: 'KPI',
    items: [
      { label: 'CO2 (T/CO2)', keys: ['co2_kg', 'reduced_co2', 'co2_reduced', 'co2_emission_reduced', 'co2Reduced'], formatCo2Tons: true },
    ]
  },
];

// Flatten payload për kërkim (edhe nested: payload.voltage.uab -> voltage_uab)
function flattenPayload(obj, prefix = '') {
  const out = {};
  if (!obj || typeof obj !== 'object') return out;
  for (const [k, v] of Object.entries(obj)) {
    const key = (prefix ? prefix + '_' : '') + String(k).replace(/\s/g, '_').toLowerCase();
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, flattenPayload(v, key));
    } else {
      out[key] = v;
      out[k] = v; // edhe key origjinal
    }
  }
  return out;
}

function getPayloadValue(flat, keys) {
  for (const k of keys) {
    if (flat[k] != null) return String(flat[k]);
    const lower = k.toLowerCase();
    if (flat[lower] != null) return String(flat[lower]);
    const withUnderscore = lower.replace(/\s/g, '_');
    if (flat[withUnderscore] != null) return String(flat[withUnderscore]);
  }
  return '—';
}

/** Vlera e parë numerike nga çelësat (MW për *_mw; total_active_power në kW → kthehet si MW). */
/** Si getPowerFromPayloadForGraph: *_mw në MW; të tjerat në kW → MW. */
function getFirstPowerMwFromFlat(flat, keys) {
  for (const k of keys) {
    const kl = String(k).toLowerCase();
    const raw = flat[k] ?? flat[kl];
    if (raw == null || raw === '') continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    if (kl.endsWith('_mw')) return n;
    return n / 1000;
  }
  return null;
}

/** Shfaqje sipas display_power_unit të Data Logger (MW ose kW). */
function formatDlPowerMwDisplay(mwSigned, displayUnit) {
  if (!Number.isFinite(mwSigned)) return '—';
  const useKw = normalizeDisplayPowerUnit(displayUnit) === 'kw';
  const labs = powerEnergyLabels(displayUnit);
  const val = useKw ? mwSigned * 1000 : mwSigned;
  const decimals = useKw ? 3 : 4;
  return `${Number(val).toFixed(decimals)} ${labs.power}`;
}

// Nxjerr fuqinë si numër nga payload për grafik (kW) – total prodhimi i të gjitha inverterave
function getPowerFromPayloadForGraph(payload) {
  const flat = flattenPayload(payload || {});
  const keys = ['total_active_power', 'active_power_mw', 'dc_power_mw', 'input_power_mw', 'pvPower', 'active_power', 'power', 'input_power', 'pv_power'];
  for (const k of keys) {
    const v = flat[k];
    if (v == null) continue;
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isNaN(n)) {
      if (k === 'active_power_mw' || k === 'input_power_mw' || k === 'dc_power_mw') return Math.abs(n * 1000);
      return Math.abs(n);
    }
  }
  return 0;
}

function isWithinProductionHours(rt) {
  if (!rt || !(rt instanceof Date) || isNaN(rt.getTime())) return false;
  const h = rt.getHours();
  const m = rt.getMinutes();
  if (h < 6) return false;
  if (h > 20) return false;
  if (h === 20 && m > 0) return false;
  return true;
}

// Ndërton të dhëna grafiku Data Logger – total prodhimi, logjika si Inverter Detail
function buildDataLoggerChartData(readings, selectedDate = null, period = 'Day') {
  if (!readings || !readings.length) return [{ time: '—', name: '—', value: 0 }];
  let filtered = readings;
  const refDate = selectedDate ? new Date(selectedDate) : new Date();

  if (period === 'Day') {
    const dayStart = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return !!rt && rt >= dayStart && rt < dayEnd;
    });
  } else if (period === 'Month') {
    const monthStart = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
    const monthEnd = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0, 23, 59, 59);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt || rt < monthStart || rt > monthEnd) return false;
      return isWithinProductionHours(rt);
    });
  } else if (period === 'Year') {
    const yearStart = new Date(refDate.getFullYear(), 0, 1);
    const yearEnd = new Date(refDate.getFullYear(), 11, 31, 23, 59, 59);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt || rt < yearStart || rt > yearEnd) return false;
      return isWithinProductionHours(rt);
    });
  } else if (period === 'Week') {
    const weekEnd = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), 23, 59, 59);
    const weekStart = new Date(weekEnd);
    weekStart.setDate(weekStart.getDate() - 6);
    weekStart.setHours(0, 0, 0, 0);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return rt && rt >= weekStart && rt <= weekEnd && isWithinProductionHours(rt);
    });
  } else {
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return isWithinProductionHours(rt);
    });
  }

  // Day: 00:00–23:59 (bucket 5 min: 00:00, 00:05, 00:10 …)
  if (period === 'Day') {
    const now = new Date();
    const isToday =
      refDate.getFullYear() === now.getFullYear() &&
      refDate.getMonth() === now.getMonth() &&
      refDate.getDate() === now.getDate();
    const endOfDay = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), 23, 59, 59);
    const stopAt = isToday ? now : endOfDay;
    const bySlot = {};
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt) return;
      const m5 = Math.floor(rt.getMinutes() / 5) * 5;
      const key = `${String(rt.getHours()).padStart(2, '0')}:${String(m5).padStart(2, '0')}`;
      if (!bySlot[key]) bySlot[key] = { sum: 0, count: 0 };
      bySlot[key].sum += getPowerFromPayloadForGraph(r.payload);
      bySlot[key].count += 1;
    });
    const result = [];
    for (let h = 0; h <= 23; h++) {
      for (let m = 0; m < 60; m += 5) {
        const slotStart = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), h, m, 0);
        if (slotStart > stopAt) return result.length ? result : [{ time: '00:00', name: '00:00', value: 0 }];
        const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        const slot = bySlot[timeStr];
        const avg = slot && slot.count > 0 ? slot.sum / slot.count : 0;
        result.push({ time: timeStr, name: timeStr, value: avg });
      }
    }
    return result.length ? result : [{ time: '00:00', name: '00:00', value: 0 }];
  }

  // Month: ditë 01 deri sot kur është muaji aktual, përndryshe 01–31
  if (period === 'Month') {
    const now = new Date();
    const daysInMonth = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0).getDate();
    const isCurrentMonth = refDate.getFullYear() === now.getFullYear() && refDate.getMonth() === now.getMonth();
    const endDay = isCurrentMonth ? now.getDate() : daysInMonth;
    const byDay = {};
    for (let day = 1; day <= daysInMonth; day++) byDay[day] = { sum: 0, count: 0 };
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt) return;
      if (isWithinProductionHours(rt)) {
        const day = rt.getDate();
        byDay[day].sum += getPowerFromPayloadForGraph(r.payload);
        byDay[day].count += 1;
      }
    });
    const result = [];
    const yr = refDate.getFullYear();
    const mo = refDate.getMonth() + 1;
    for (let day = 1; day <= endDay; day++) {
      const avg = byDay[day].count > 0 ? byDay[day].sum / byDay[day].count : 0;
      const nameStr = `${String(day).padStart(2, '0')}.${String(mo).padStart(2, '0')}.${String(yr).slice(-2)}`;
      result.push({ time: nameStr, name: nameStr, value: avg });
    }
    return result.length ? result : [{ time: '01', name: '01', value: 0 }];
  }

  // Year: Jan deri në muajin aktual kur është viti aktual, përndryshe 12 muaj
  if (period === 'Year') {
    const now = new Date();
    const isCurrentYear = refDate.getFullYear() === now.getFullYear();
    const endMonth = isCurrentYear ? now.getMonth() : 11;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Qer', 'Kor', 'Gus', 'Sht', 'Tet', 'Nën', 'Dhj'];
    const byMonth = {};
    for (let m = 0; m < 12; m++) byMonth[m] = { sum: 0, count: 0 };
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt) return;
      if (isWithinProductionHours(rt)) {
        byMonth[rt.getMonth()].sum += getPowerFromPayloadForGraph(r.payload);
        byMonth[rt.getMonth()].count += 1;
      }
    });
    const result = [];
    for (let m = 0; m <= endMonth; m++) {
      const avg = byMonth[m].count > 0 ? byMonth[m].sum / byMonth[m].count : 0;
      result.push({ time: `${monthNames[m]} ${refDate.getFullYear()}`, name: `${monthNames[m]} ${refDate.getFullYear()}`, value: avg });
    }
    return result;
  }

  // Week: 7 ditë
  if (period === 'Week') {
    const byDay = {};
    for (let i = 0; i < 7; i++) {
      const d = new Date(refDate);
      d.setDate(d.getDate() - 6 + i);
      byDay[d.toDateString()] = { sum: 0, count: 0 };
    }
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt || !byDay[rt.toDateString()]) return;
      byDay[rt.toDateString()].sum += getPowerFromPayloadForGraph(r.payload);
      byDay[rt.toDateString()].count += 1;
    });
    const result = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(refDate);
      d.setDate(d.getDate() - 6 + i);
      const b = byDay[d.toDateString()] || { sum: 0, count: 0 };
      const avg = b.count > 0 ? b.sum / b.count : 0;
      const nameStr = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
      result.push({ time: nameStr, name: nameStr, value: avg });
    }
    return result;
  }

  // Total / Lifetime: 12 muajt e fundit
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Qer', 'Kor', 'Gus', 'Sht', 'Tet', 'Nën', 'Dhj'];
  const byKey = {};
  filtered.forEach((r) => {
    const rt = r.ts ? new Date(r.ts) : null;
    if (!rt) return;
    if (isWithinProductionHours(rt)) {
      const key = `${rt.getFullYear()}-${String(rt.getMonth() + 1).padStart(2, '0')}`;
      if (!byKey[key]) byKey[key] = { sum: 0, count: 0 };
      byKey[key].sum += getPowerFromPayloadForGraph(r.payload);
      byKey[key].count += 1;
    }
  });
  const now = new Date();
  const result = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const b = byKey[key] || { sum: 0, count: 0 };
    const avg = b.count > 0 ? b.sum / b.count : 0;
    const nameStr = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
    result.push({ time: nameStr, name: nameStr, value: avg });
  }
  return result;
}

/** Energjia totale e prodhuar (MWh) nga payload — i njëjti burim si kartat e energjisë. */
function getDlTotalMWhFromPayload(payload) {
  const flat = flattenPayload(payload || {});
  const get = (keys, fallback = '0') => {
    const v = getPayloadValue(flat, keys);
    return v === '—' ? fallback : v;
  };
  const toNum = (v) => (v == null || v === '' || Number.isNaN(Number(v))) ? 0 : Number(v);
  if (flat.energy_total_mwh != null) return toNum(flat.energy_total_mwh);
  if (flat.energy_total_kwh != null) return toNum(flat.energy_total_kwh) / 1000;
  return toNum(get(['total_energy_yield', 'total_energy', 'e_total', 'exported_energy', 'total_export', 'output_total', 'pv_yield_total'], '0')) / 1000;
}

// Main metrics for Data Logger cards – MW/MWh/MVar ose kW/kWh/kVAR sipas display_power_unit të pajisjes
function buildDataLoggerReadings(payload, displayUnit = 'mw') {
  const flat = flattenPayload(payload || {});
  const get = (keys, fallback = '0') => {
    const v = getPayloadValue(flat, keys);
    return v === '—' ? fallback : v;
  };
  const toNum = (v) => (v == null || v === '' || Number.isNaN(Number(v))) ? 0 : Number(v);
  const fmtMWh = (n) => String(Math.round(n * 100) / 100);
  const fmtMW = (n) => String(Math.round(n * 1000) / 1000);
  const useKw = normalizeDisplayPowerUnit(displayUnit) === 'kw';
  // Energy: MWh. Burime: energy_day_mwh (MWh), energy_day_kwh (kWh→/1000), legacy keys (kWh→/1000)
  const dayMWh = flat.energy_day_mwh != null
    ? toNum(flat.energy_day_mwh)
    : (flat.energy_day_kwh != null ? toNum(flat.energy_day_kwh) : toNum(get(['energy_yield_of_current_day', 'today_energy', 'e_today', 'energy_today', 'pv_yield_today', 'output_today'], '0'))) / 1000;
  const totalMWh = getDlTotalMWhFromPayload(payload);
  // Power: MW (nëse vjen kW, pjestoj me 1000). active_power_mw/input_power_mw janë tashmë MW
  const pwKw = toNum(get(['total_active_power', 'active_power', 'power', 'pvPower', 'outputAC'], '0'));
  const pwMw = flat.active_power_mw != null || flat.input_power_mw != null
    ? toNum(flat.active_power_mw ?? flat.input_power_mw)
    : pwKw / 1000;
  // Reactive: MVar (nëse vjen kVar, pjestoj me 1000)
  const qKvar = toNum(get(['total_reactive_power', 'reactive_power', 'q_total'], '0'));
  const qMvar = flat.reactive_power_mvar != null ? toNum(flat.reactive_power_mvar) : qKvar / 1000;
  if (useKw) {
    return {
      active_power: fmtMW(Math.abs(pwMw) * 1000),
      reactive_power: fmtMW(qMvar * 1000),
      power_factor: get(['remote_sched_pf', 'total_power_factor', 'power_factor', 'pf'], '0'),
      today_energy: fmtMWh(dayMWh * 1000),
      total_energy: fmtMWh(totalMWh * 1000),
      energy_yield_of_current_day: fmtMWh(dayMWh * 1000),
      total_energy_yield: fmtMWh(totalMWh * 1000),
      feed_in_power_limit: get(['remote_sched_p_mw', 'feed_in_limit', 'remote_scheduling_p'], '0.00'),
      device_status: getPayloadValue(flat, ['device_status', 'plant_status_text', 'plant_status']),
      co2_kg: get(['co2_kg', 'reduced_co2', 'co2_reduced', 'co2_emission_reduced', 'co2Reduced'], '0'),
    };
  }
  return {
    active_power: fmtMW(Math.abs(pwMw)),
    reactive_power: fmtMW(qMvar),
    power_factor: get(['remote_sched_pf', 'total_power_factor', 'power_factor', 'pf'], '0'),
    today_energy: fmtMWh(dayMWh),
    total_energy: fmtMWh(totalMWh),
    energy_yield_of_current_day: fmtMWh(dayMWh),
    total_energy_yield: fmtMWh(totalMWh),
    feed_in_power_limit: get(['remote_sched_p_mw', 'feed_in_limit', 'remote_scheduling_p'], '0.00'),
    device_status: getPayloadValue(flat, ['device_status', 'plant_status_text', 'plant_status']),
    co2_kg: get(['co2_kg', 'reduced_co2', 'co2_reduced', 'co2_emission_reduced', 'co2Reduced'], '0'),
  };
}

// Kthen të njëjtin dizajn (seksione + etiketa) me vlera nga payload ose "—"
function buildMeterSectionsWithPayload(payload, displayUnit = 'mw') {
  const flat = flattenPayload(payload || {});
  return DATALOGGER_DETAIL_SECTIONS.map((section) => ({
    name: section.name,
    items: section.items.map((item) => {
      let value = '—';
      if (item.formatMwField && item.keys?.length) {
        const mw = getFirstPowerMwFromFlat(flat, item.keys);
        value = mw == null ? '—' : formatDlPowerMwDisplay(mw, displayUnit);
      } else if (item.keys) {
        const raw = getPayloadValue(flat, item.keys);
        value = raw != null && raw !== '' ? raw : '—';
        if (item.formatCo2Tons && raw != null && raw !== '') {
          const tons = safeToFixed(Number(raw) / 1000, 2);
          value = `${tons} T/CO2`;
        }
      }
      return { label: item.label, value };
    }),
  }));
}

// Mobile: dizajni origjinal – 3 seksione Real-time (Voltage, Current, Power)
const FIXED_MOBILE_REALTIME = [
  {
    name: 'Voltage', items: [
      { label: 'Line Voltage Uab', keys: ['line_voltage_uab', 'voltage_uab', 'uab'] },
      { label: 'Line Voltage Ubc', keys: ['line_voltage_ubc', 'voltage_ubc', 'ubc'] },
      { label: 'Line Voltage Uca', keys: ['line_voltage_uca', 'voltage_uca', 'uca'] },
    ]
  },
  {
    name: 'Current', items: [
      { label: 'Phase Current Ia', keys: ['phase_current_ia', 'current_ia', 'ia'] },
      { label: 'Phase Current Ib', keys: ['phase_current_ib', 'current_ib', 'ib'] },
      { label: 'Phase Current Ic', keys: ['phase_current_ic', 'current_ic', 'ic'] },
    ]
  },
  {
    name: 'Power', items: [
      { label: 'Total Active Power', keys: ['total_active_power', 'active_power', 'power'] },
      { label: 'Power Factor', keys: ['total_power_factor', 'power_factor', 'pf'] },
      { label: 'Frequency', keys: ['frequency', 'freq', 'hz'] },
    ]
  },
];

const FIXED_ENERGY_ITEMS = [
  { label: 'Imported Energy', keys: ['imported_energy', 'total_import', 'import_total'] },
  { label: 'Imported Energy(R)', keys: ['imported_energy_r', 'import_r'] },
  { label: 'Imported Energy(S)', keys: ['imported_energy_s', 'import_s'] },
  { label: 'Imported Energy(T)', keys: ['imported_energy_t', 'import_t'] },
  { label: 'Exported Energy', keys: ['exported_energy', 'total_export', 'export_total'] },
  { label: 'Exported Energy(R)', keys: ['exported_energy_r', 'export_r'] },
  { label: 'Exported Energy(S)', keys: ['exported_energy_s', 'export_s'] },
  { label: 'Exported Energy(T)', keys: ['exported_energy_t', 'export_t'] },
];

function buildMobileRealtimeFromPayload(payload) {
  const flat = flattenPayload(payload || {});
  return FIXED_MOBILE_REALTIME.map((s) => ({
    name: s.name,
    items: s.items.map((i) => ({ label: i.label, value: getPayloadValue(flat, i.keys) })),
  }));
}

function buildMobileEnergyFromPayload(payload) {
  const flat = flattenPayload(payload || {});
  return FIXED_ENERGY_ITEMS.map((i) => ({ label: i.label, value: getPayloadValue(flat, i.keys) }));
}

function formatTimeAgo(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  return `${Math.floor(sec / 86400)} days ago`;
}

// Nxjerr status të shkurtër nga inverter reading payload (Standby: No irradiation -> Standby)
function getInverterStatusShort(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const flat = flattenPayload(payload);
  const raw = flat.device_status_text ?? flat.device_status ?? flat.state ?? flat.status ?? flat.run_state;
  if (raw == null || raw === '') return null;
  const s = String(raw);
  return s.includes(':') ? s.split(':')[0].trim() : s;
}

// Nxjerr Today + Total yield nga inverter payload (në MWh)
function getInverterYieldFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return { todayMWh: 0, totalMWh: 0 };
  const flat = flattenPayload(payload);
  const toNum = (v) => (v == null || v === '' || Number.isNaN(Number(v))) ? 0 : Number(v);
  const dayKwh = toNum(flat.energy_day_kwh ?? flat.e_today ?? flat.today_yield ?? flat.energy_today ?? flat.pv_yield_today ?? flat.output_today);
  const dayMWh = flat.energy_day_mwh != null ? toNum(flat.energy_day_mwh) : dayKwh / 1000;
  const totalKwh = toNum(flat.energy_total_kwh ?? flat.e_total ?? flat.total_yield ?? flat.energy_total ?? flat.pv_yield_total ?? flat.output_total);
  const totalMWh = flat.energy_total_mwh != null ? toNum(flat.energy_total_mwh) : totalKwh / 1000;
  return { todayMWh: dayMWh, totalMWh };
}

const DataLoggerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [dataLogger, setDataLogger] = useState(null);
  const [company, setCompany] = useState(null);
  const [companiesList, setCompaniesList] = useState([]);
  const [inverters, setInverters] = useState([]);
  const [inverterReadingsMap, setInverterReadingsMap] = useState({});
  const [readings, setReadings] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [alarmHistory, setAlarmHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));
  const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const dlStorageKey = id ? `chart-dl-${id}` : null;

  const [mainTab, setMainTab] = useState(() => {
    if (typeof window !== 'undefined' && dlStorageKey) {
      try {
        const s = localStorage.getItem(dlStorageKey);
        if (s) {
          const p = JSON.parse(s);
          if (p?.mainTab) return p.mainTab;
        }
      } catch (_) { }
    }
    return 'details';
  });
  const [graphPeriod, setGraphPeriod] = useState(() => {
    if (typeof window !== 'undefined' && dlStorageKey) {
      try {
        const s = localStorage.getItem(dlStorageKey);
        if (s) {
          const p = JSON.parse(s);
          if (p?.graphPeriod) return p.graphPeriod;
        }
      } catch (_) { }
    }
    return 'Day';
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    if (typeof window !== 'undefined' && dlStorageKey) {
      try {
        const s = localStorage.getItem(dlStorageKey);
        if (s) {
          const p = JSON.parse(s);
          if (p?.selectedDate) return p.selectedDate;
        }
      } catch (_) { }
    }
    return todayStr();
  });
  const [chartReadings, setChartReadings] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  const saveChartPrefs = useCallback(() => {
    if (dlStorageKey && typeof window !== 'undefined') {
      try {
        localStorage.setItem(dlStorageKey, JSON.stringify({
          selectedDate,
          graphPeriod,
          mainTab,
        }));
      } catch (_) { }
    }
  }, [dlStorageKey, selectedDate, graphPeriod, mainTab]);

  useEffect(() => {
    saveChartPrefs();
  }, [saveChartPrefs]);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData defined below; id drives reload
  }, [id]);

  // Cache i shkurtër për grafik (eviton refetch kur kthehet i njëjti interval) – 2 min
  const chartCacheRef = useRef({ key: '', data: null, ts: 0 });
  const datePickerRef = useRef(null);
  const monthPickerRef = useRef(null);

  // Për grafik: fetch me debounce + anulim, pa overload në API / mobile
  useEffect(() => {
    if (!id || !selectedDate) return;
    const sel = new Date(selectedDate);
    let dateFrom = '';
    let dateTo = '';
    if (graphPeriod === 'Day') {
      dateFrom = `${selectedDate}T00:00:00`;
      dateTo = `${selectedDate}T23:59:59`;
    } else if (graphPeriod === 'Month') {
      const y = sel.getFullYear();
      const m = sel.getMonth();
      dateFrom = `${y}-${String(m + 1).padStart(2, '0')}-01T00:00:00`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      dateTo = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59:59`;
    } else if (graphPeriod === 'Year') {
      dateFrom = `${sel.getFullYear()}-01-01T00:00:00`;
      dateTo = `${sel.getFullYear()}-12-31T23:59:59`;
    } else if (graphPeriod === 'Week') {
      const end = new Date(sel);
      const start = new Date(end);
      start.setDate(start.getDate() - 6);
      dateFrom = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}T00:00:00`;
      dateTo = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}T23:59:59`;
    } else if (graphPeriod === 'Total') {
      const now = new Date();
      dateFrom = '2020-01-01T00:00:00';
      dateTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T23:59:59`;
    } else {
      setChartReadings(null);
      return;
    }
    const chartKey = `${id}|${dateFrom}|${dateTo}`;
    const cached = chartCacheRef.current;
    if (cached.key === chartKey && cached.data !== null && Date.now() - cached.ts < CHART_CACHE_TTL_MS) {
      setChartReadings(cached.data);
      return;
    }
    setChartLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const getToken = () => localStorage.getItem('access_token');
      axios
        .get(
          `${BACKEND_URL}/api/devices/${id}/pi-datalogger-readings?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`,
          { headers: { Authorization: `Bearer ${getToken()}` }, signal: controller.signal },
        )
        .then((res) => {
          const data = res.data?.readings ?? [];
          chartCacheRef.current = { key: chartKey, data, ts: Date.now() };
          setChartReadings(data);
          setChartLoading(false);
        })
        .catch((err) => {
          setChartLoading(false);
          if (axios.isCancel(err)) return;
          setChartReadings([]);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
      setChartLoading(false);
    };
  }, [id, selectedDate, graphPeriod]);

  const getToken = () => localStorage.getItem('access_token');

  const fetchData = async () => {
    if (!id) return;
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const [dlRes, invRes, compRes, readingsRes, alarmsPiRes, alarmsApiRes] = await Promise.all([
        cachedAxiosGet(axios, `${BACKEND_URL}/api/devices/data-loggers/${id}`, { headers }, { ttlMs: 90_000 }),
        cachedAxiosGet(axios, `${BACKEND_URL}/api/devices/inverters?data_logger_id=${encodeURIComponent(id)}`, { headers }, { ttlMs: 60_000 }),
        cachedAxiosGet(axios, `${BACKEND_URL}/api/companies`, { headers }, { ttlMs: 120_000 }),
        axios.get(`${BACKEND_URL}/api/devices/${id}/pi-datalogger-readings?limit=500`, { headers }).catch(() => ({ data: { readings: [] } })),
        axios.get(`${BACKEND_URL}/api/devices/${id}/pi-alarms-datalogger?limit=50`, { headers }).catch(() => ({ data: { alarms: [] } })),
        axios.get(`${BACKEND_URL}/api/alarms?device_id=${id}&device_type=DATA_LOGGER&limit=50`, { headers }).catch(() => ({ data: [] })),
      ]);
      setDataLogger(dlRes.data);
      const rawInv = Array.isArray(invRes.data) ? invRes.data : [];
      const dlInverters = rawInv.filter(
        (inv) => String(inv?.data_logger_id ?? inv?.dataLoggerId ?? '') === String(id),
      );
      setInverters(dlInverters);
      const compData = compRes.data || [];
      setCompaniesList(Array.isArray(compData) ? compData : []);
      const comp = compData.find(c => c.id === dlRes.data.company_id);
      setCompany(comp);
      setReadings(readingsRes.data?.readings ?? []);
      const piAlarmsRaw = alarmsPiRes.data?.alarms ?? [];
      const piAlarms = [];
      piAlarmsRaw.forEach((doc) => {
        const list = Array.isArray(doc?.payload?.alarms) ? doc.payload.alarms : [];
        list.forEach((a) => piAlarms.push({ ...a, _ts: doc?.ts }));
        if (list.length === 0 && doc?.payload && (doc.payload.name != null || doc.payload.message != null)) {
          piAlarms.push({ ...doc.payload, _ts: doc?.ts });
        }
      });
      const apiAlarms = Array.isArray(alarmsApiRes.data) ? alarmsApiRes.data : [];
      const merged = apiAlarms.map((a) => ({ name: a?.message, message: a?.message, state: a?.is_active ? 'ON' : 'OFF', ...a }));
      piAlarms.forEach((a) => {
        if (!merged.some((m) => (m?.id && m.id === a?.id) || (m?.no === a?.no && String(m?.name || '').trim() === String(a?.name || a?.message || '').trim()))) merged.push(a);
      });
      setAlarms(merged);
      const history = (piAlarmsRaw || []).map((doc) => {
        const pl = doc?.payload || {};
        const alarmsList = Array.isArray(pl.alarms) ? pl.alarms : [];
        const anyOn = alarmsList.some((a) => String(a?.state || '').toUpperCase() === 'ON');
        return { ts: doc?.ts, active: !!anyOn };
      });
      setAlarmHistory(history);
      const invReadings = {};
      await Promise.all(dlInverters.map(async (inv) => {
        try {
          const r = await axios.get(`${BACKEND_URL}/api/devices/${inv.id}/pi-inverter-readings?limit=1`, { headers });
          const items = r.data?.readings ?? [];
          if (items[0]?.payload) {
            const p = items[0].payload;
            const status = getInverterStatusShort(p);
            const yieldVal = getInverterYieldFromPayload(p);
            invReadings[inv.id] = { status, ...yieldVal };
          }
        } catch (_) { /* ignore */ }
      }));
      setInverterReadingsMap(invReadings);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const latestReading = readings.length > 0 ? readings[0] : null;
  const powerDisplayUnit = normalizeDisplayPowerUnit(dataLogger?.display_power_unit);
  const labels = powerEnergyLabels(powerDisplayUnit);
  const baseReadings = buildDataLoggerReadings(latestReading?.payload, powerDisplayUnit);
  const _sumTodayMWh = inverters.reduce((s, inv) => {
    const r = inverterReadingsMap[inv.id];
    return s + ((r && typeof r === 'object' && typeof r.todayMWh === 'number') ? r.todayMWh : 0);
  }, 0);
  const _sumTotalMWh = inverters.reduce((s, inv) => {
    const r = inverterReadingsMap[inv.id];
    return s + ((r && typeof r === 'object' && typeof r.totalMWh === 'number') ? r.totalMWh : 0);
  }, 0);
  const fmtYieldCard = (mwh) => {
    if (powerDisplayUnit === 'kw') return String(Math.round(mwh * 1000 * 100) / 100);
    return String(Math.round(mwh * 100) / 100);
  };
  const hasInverterYields = Object.keys(inverterReadingsMap).length > 0;
  const safeReadings = hasInverterYields
    ? {
      ...baseReadings,
      energy_yield_of_current_day: fmtYieldCard(_sumTodayMWh),
      total_energy_yield: fmtYieldCard(_sumTotalMWh),
      today_energy: fmtYieldCard(_sumTodayMWh),
      total_energy: fmtYieldCard(_sumTotalMWh),
    }
    : baseReadings;

  const meterSections = buildMeterSectionsWithPayload(latestReading?.payload, powerDisplayUnit);
  const lastUpdated = formatTimeAgo(latestReading?.ts) || null;

  const readingsForChart = chartReadings !== null ? chartReadings : readings;
  const webGraphData = buildDataLoggerChartData(readingsForChart, selectedDate, graphPeriod);
  const webChartDataKey = graphPeriod === 'Day' ? 'time' : 'name';

  const changeDate = (delta) => {
    setSelectedDate((prev) => {
      const base = prev || todayStr();
      const d = new Date(`${base}T12:00:00`);
      if (Number.isNaN(d.getTime())) return todayStr();
      if (graphPeriod === 'Day') d.setDate(d.getDate() + delta);
      else if (graphPeriod === 'Week') d.setDate(d.getDate() + 7 * delta);
      else if (graphPeriod === 'Month') d.setMonth(d.getMonth() + delta);
      else if (graphPeriod === 'Year') d.setFullYear(d.getFullYear() + delta);
      return clampDlChartDate(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
        graphPeriod,
      );
    });
  };

  const openWebDlPeriodPicker = () => {
    if (graphPeriod === 'Day' || graphPeriod === 'Week') {
      datePickerRef.current?.showPicker?.();
      datePickerRef.current?.click();
    } else if (graphPeriod === 'Month') {
      monthPickerRef.current?.showPicker?.();
      monthPickerRef.current?.click();
    }
  };

  const today = new Date();
  const dateStrDisplay = selectedDate || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const dDisplay = new Date(selectedDate || dateStrDisplay);
  const graphDateDisplay =
    graphPeriod === 'Day'
      ? dateStrDisplay
      : graphPeriod === 'Week'
        ? `${String(Math.max(1, dDisplay.getDate() - 6)).padStart(2, '0')}.${String(dDisplay.getMonth() + 1).padStart(2, '0')} ~ ${String(dDisplay.getDate()).padStart(2, '0')}.${String(dDisplay.getMonth() + 1).padStart(2, '0')}`
        : graphPeriod === 'Month'
          ? `${String(dDisplay.getMonth() + 1).padStart(2, '0')}.${dDisplay.getFullYear()}`
          : graphPeriod === 'Year'
            ? String(dDisplay.getFullYear())
            : 'Total';

  const bgMain = isDark ? 'bg-[#0f172a]' : 'bg-gray-100';
  const bgCard = isDark ? 'bg-[#1e293b]' : 'bg-white';
  const bgCardAlt = isDark ? 'bg-[#334155]' : 'bg-gray-50';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const borderColor = isDark ? 'border-[#334155]' : 'border-gray-200';

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-full"><p className="text-gray-500">{t('loading')}</p></div></Layout>;
  }

  const onlineCount = inverters.filter(inv => inv.status === 'ONLINE').length;
  const faultCount = inverters.filter(inv => inv.status === 'FAULT').length;
  const offlineCount = inverters.filter(inv => inv.status === 'OFFLINE').length;
  const inverterStatusCounts = inverters.reduce((acc, inv) => {
    const r = inverterReadingsMap[inv.id];
    const s = (r && typeof r === 'object') ? r.status : (r || '—');
    const key = s && s !== '—' ? s : 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const standbyCount = (inverterStatusCounts['Standby'] || 0) + (inverterStatusCounts['Idle'] || 0);

  // MOBILE VIEW - Return early if mobile
  if (isMobile) {
    return (
      <MobileDataLoggerView
        dataLogger={dataLogger}
        company={company}
        companiesList={companiesList}
        inverters={inverters}
        inverterStatusCounts={inverterStatusCounts}
        safeReadings={safeReadings}
        meterSections={meterSections}
        navigate={navigate}
        isDark={isDark}
        readings={readings}
        alarms={alarms}
        alarmHistory={alarmHistory}
        lastUpdated={lastUpdated}
        fetchData={fetchData}
        powerDisplayUnit={powerDisplayUnit}
        t={t}
      />
    );
  }

  // WEB VIEW (Desktop) - Original design
  return (
    <Layout>
      <div className={`h-full ${bgMain} overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className={`${bgCard} border-b ${borderColor} px-4 py-2 flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <button onClick={() => company?.id ? navigate(`/devices?company=${company.id}`) : navigate('/devices')} className={`flex items-center gap-1 ${textSecondary} hover:text-white text-sm`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">{t('dataLogger')}</span>
            </button>
            <span className={`${textSecondary} text-sm`}>SN: {dataLogger?.serial_number}</span>
            <div className="flex-1"></div>
            {lastUpdated && <span className={`${textSecondary} text-xs`}>{t('updatedLabel')} {lastUpdated}</span>}
          </div>
        </div>

        {/* Content */}
        <div className="flex gap-4 p-4 flex-1 overflow-hidden">
          {/* Left Panel - Data Logger Card */}
          <div className={`w-80 ${bgCard} rounded-xl overflow-hidden flex-shrink-0 flex flex-col`}>
            <div className="relative p-4 flex-shrink-0">
              <div className="absolute top-3 right-3">
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                </svg>
              </div>
              {/* Data Logger Image - actual photo from database */}
              <div className="mx-auto w-48 h-32 flex items-center justify-center rounded-lg overflow-hidden">
                {dataLogger?.photo_url ? (
                  <img
                    src={`${BACKEND_URL}${dataLogger.photo_url}`}
                    alt={dataLogger.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className={`w-full h-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center`}>
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                )}
              </div>
              <h2 className={`text-lg font-bold text-center mt-3 ${textPrimary}`}>{t('dataLogger')}</h2>
              <p className={`text-xs text-center ${textSecondary}`}>{dataLogger?.name}</p>
            </div>

            {/* Device Information - ngjitur më lart */}
            <div className={`px-4 py-3 border-t ${borderColor} space-y-2 flex-shrink-0`}>
              <h3 className={`font-semibold ${textPrimary} text-sm mb-2`}>{t('deviceInfo')}</h3>
              <div className="flex justify-between text-sm">
                <span className={textSecondary}>{t('modelLabel')}:</span>
                <span className={textPrimary}>{dataLogger?.model || dataLogger?.name || '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className={textSecondary}>{t('ipLabel')}:</span>
                <span className={textPrimary}>{dataLogger?.ip_address || '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className={textSecondary}>{t('unitId')}:</span>
                <span className={textPrimary}>{dataLogger?.unit_id ?? '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className={textSecondary}>{t('portLabel')}:</span>
                <span className={textPrimary}>{dataLogger?.port ?? '—'}</span>
              </div>
            </div>

            <div className={`px-4 py-3 flex justify-between items-center border-t ${borderColor} flex-shrink-0`}>
              <p className={`text-xs ${textSecondary}`}>{t('status')}</p>
              <span className="px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg text-sm font-medium inline-flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>
                <span className="truncate" title={safeReadings.device_status === '—' || !safeReadings.device_status ? t('online') : safeReadings.device_status}>
                  {safeReadings.device_status === '—' || !safeReadings.device_status ? t('online') : safeReadings.device_status}
                </span>
              </span>
            </div>

            <div className={`px-4 py-3 border-t ${borderColor} flex-1`}>
              <div className="flex justify-between text-sm mb-2">
                <span className={textSecondary}>{t('total')}</span>
                <span className={`font-bold ${textPrimary}`}>{inverters.length}</span>
              </div>
              <div className={`flex justify-between items-center px-3 py-2 rounded-lg text-sm mb-2 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                <span className={`${textPrimary} flex items-center gap-2 font-medium`}>{t('totalInverter')}</span>
                <span className={`font-bold ${textPrimary}`}>
                  {powerDisplayUnit === 'kw'
                    ? `${safeToFixed(sumInverterRatedPowerKw(inverters), 2)} kW`
                    : `${safeToFixed(sumInverterRatedPowerKw(inverters) / 1000, 2)} MW`}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center px-3 py-2 bg-green-500/10 rounded-lg text-sm">
                  <span className="text-green-500 flex items-center gap-2"><span className="w-2 h-2 bg-green-500 rounded-full"></span>{t('online')}</span>
                  <span className="font-bold">
                    {offlineCount === 0 ? (
                      <span className="text-green-500">{onlineCount}</span>
                    ) : (
                      <><span className="text-green-500">{onlineCount}</span><span className="text-gray-400 mx-0.5">/</span><span className="text-red-500">{offlineCount}</span></>
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center px-3 py-2 bg-red-500/10 rounded-lg text-sm">
                  <span className="text-red-500 flex items-center gap-2"><span className="w-2 h-2 bg-red-500 rounded-full"></span>{t('fault')}</span>
                  <span className="font-bold text-red-500">{faultCount}</span>
                </div>
                <div className="flex justify-between items-center px-3 py-2 bg-gray-500/10 rounded-lg text-sm">
                  <span className={`${textSecondary} flex items-center gap-2`}><span className="w-2 h-2 bg-gray-400 rounded-full"></span>{t('standby')}</span>
                  <span className={`font-bold ${textPrimary}`}>{standbyCount}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Tabs: Details | Real Time | Alarms */}
          <div className={`flex-1 flex flex-col ${mainTab === 'alarms' ? 'overflow-visible' : 'overflow-hidden'}`}>
            {/* Tab Buttons - vetëm për web */}
            <div className="flex border-b mb-4 flex-shrink-0" style={{ borderColor: isDark ? '#334155' : '#e5e7eb' }}>
              <button
                onClick={() => setMainTab('details')}
                className={`px-4 py-2 text-sm font-medium ${mainTab === 'details' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : textSecondary}`}
              >
                {t('details')}
              </button>
              <button
                onClick={() => setMainTab('realtime')}
                className={`px-4 py-2 text-sm font-medium ${mainTab === 'realtime' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : textSecondary}`}
              >
                {t('realTime')}
              </button>
              <button
                onClick={() => setMainTab('alarms')}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${mainTab === 'alarms' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : textSecondary}`}
              >
                {t('alarms')} <span className={`text-xs px-1.5 py-0.5 rounded ${(alarms?.filter((a) => String(a?.state ?? 'OFF').toUpperCase() === 'ON').length || 0) > 0 ? 'bg-red-500/80 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-400'}`}>{alarms?.filter((a) => String(a?.state ?? 'OFF').toUpperCase() === 'ON').length ?? 0}</span>
              </button>
            </div>

            {mainTab === 'alarms' ? (
              <div className={`flex-1 rounded-xl border p-4 overflow-y-auto ${bgCard}`} style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                <h3 className={`font-semibold ${textPrimary} mb-4`}>{t('alarms')} ({alarms?.length ?? 0})</h3>
                {(alarms?.length || 0) === 0 ? (
                  <p className={`${textSecondary} text-sm`}>{t('noAlarms')}</p>
                ) : (
                  <ul className="grid grid-cols-2 gap-x-4 gap-y-1 mb-6">
                    {alarms.map((a, i) => {
                      const state = (a?.state ?? 'OFF').toUpperCase();
                      const isOn = state === 'ON';
                      return (
                        <li key={a?.id || a?.no || i} className={`flex items-center py-3 px-3 rounded-lg border ${borderColor} text-sm ${isOn ? 'bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-900/30' : ''}`}>
                          <span className={isOn ? 'text-red-700 dark:text-red-300 font-medium' : textPrimary}>{formatAlarmDisplay(a)}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {alarmHistory.length > 0 && (
                  <>
                    <h3 className={`text-sm font-semibold ${textPrimary} mb-2`}>{t('alarmHistory') || 'Histori alarmeve'}</h3>
                    <div className="rounded-lg border overflow-hidden" style={{ borderColor: isDark ? '#334155' : '#e5e7eb' }}>
                      <table className="w-full text-sm">
                        <thead className={isDark ? 'bg-slate-800/50' : 'bg-gray-50'}>
                          <tr><th className={`px-3 py-2 text-left font-medium ${textSecondary}`}>Data/ora</th><th className={`px-3 py-2 text-left font-medium ${textSecondary}`}>Status</th></tr>
                        </thead>
                        <tbody className={textPrimary}>
                          {alarmHistory.slice(0, 30).map((h, i) => (
                            <tr key={i} className={i % 2 === 0 ? (isDark ? 'bg-[#1e293b]' : 'bg-white') : (isDark ? 'bg-slate-800/30' : 'bg-gray-50/50')}>
                              <td className="px-3 py-2 whitespace-nowrap">{h.ts ? new Date(h.ts).toLocaleString('sq-AL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                              <td className={`px-3 py-2 font-medium ${h.active ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>{h.active ? (t('alarmActive') || 'Aktiv') : (t('alarmInactive') || 'Jo aktiv')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            ) : mainTab === 'details' ? (
              <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                {/* 5 Main Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-2 flex-shrink-0">
                  <div className={`${bgCard} rounded-xl p-4 border shadow-sm`} style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{safeToFixed(safeReadings.active_power, 2)} <span className="text-base font-normal ml-1">{labels.power}</span></p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t('activePower')}</p>
                  </div>
                  <div className={`${bgCard} rounded-xl p-4 border shadow-sm`} style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{safeToFixed(safeReadings.reactive_power, 2)} <span className="text-base font-normal ml-1">{labels.reactive}</span></p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t('reactivePower')}</p>
                  </div>
                  <div className={`${bgCard} rounded-xl p-4 border shadow-sm`} style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{safeReadings.power_factor}</p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t('powerFactor')}</p>
                  </div>
                  <div className={`${bgCard} rounded-xl p-4 border shadow-sm`} style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{safeToFixed(safeReadings.energy_yield_of_current_day, 2)} <span className="text-base font-normal ml-1">{labels.energy}</span></p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t('energyToday')}</p>
                  </div>
                  <div className={`${bgCard} rounded-xl p-4 border shadow-sm`} style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{safeToFixed(safeReadings.total_energy_yield, 2)} <span className="text-base font-normal ml-1">{labels.energy}</span></p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t('totalEnergyYield')}</p>
                  </div>
                  <div className={`${bgCard} rounded-xl p-4 border shadow-sm`} style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{safeToFixed(Number(safeReadings.co2_kg || 0) / 1000, 2)} <span className="text-base font-normal ml-1">T/CO2</span></p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t('co2Label')}</p>
                  </div>
                </div>

                {/* Details - Accordion (pa titull, itemet ngjitur lart) */}
                <div className={`${bgCard} rounded-xl border p-4 overflow-y-auto flex-1`} style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                  <Accordion type="multiple" defaultValue={[]} className="w-full">
                    {meterSections.map((section) => (
                      <AccordionItem key={section.name} value={section.name} className={`border-b ${borderColor}`}>
                        <AccordionTrigger className={`${textPrimary} hover:no-underline py-3`}>
                          {t(DL_SECTION_KEYS[section.name] || section.name)}
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-2">
                            {section.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-center py-1.5">
                                <span className={`text-sm ${textPrimary} pl-6`}>{t(DL_LABEL_KEYS[item.label] || item.label)}</span>
                                <span className={`text-sm font-medium ${textPrimary} mr-[30%]`}>{item.value}</span>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </div>
            ) : (
              /* Real Time - Graph */
              <div className={`flex-1 rounded-xl border p-4 overflow-hidden flex flex-col min-h-0 ${bgCard}`} style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 flex-shrink-0">
                  <h3 className={`font-semibold ${textPrimary}`}>{t('energyAnalysis')}</h3>
                  <div className="flex items-center gap-3">
                    <div className={`flex rounded-lg p-1 ${isDark ? 'bg-[#334155]' : 'bg-gray-100'}`}>
                      {['Day', 'Week', 'Month', 'Year', 'Total'].map((period) => (
                        <button
                          key={period}
                          onClick={() => setGraphPeriod(period)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium ${graphPeriod === period
                            ? isDark ? 'bg-[#1e293b] text-purple-400 shadow-sm' : 'bg-white text-purple-600 shadow-sm'
                            : isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}
                        >
                          {period === 'Day' ? t('dayLabel') : period === 'Week' ? t('weekLabel') : period === 'Month' ? t('monthLabel') : period === 'Year' ? t('yearLabel') : t('totalLabel')}
                        </button>
                      ))}
                    </div>
                    {(graphPeriod === 'Day' || graphPeriod === 'Week' || graphPeriod === 'Month' || graphPeriod === 'Year') && (
                      <div className={`flex items-center gap-2 border rounded-lg px-3 py-1.5 ${isDark ? 'border-[#334155]' : 'border-gray-200'}`}>
                        <input
                          ref={datePickerRef}
                          type="date"
                          max={todayStr()}
                          value={selectedDate || todayStr()}
                          onChange={(e) => e.target.value && setSelectedDate(clampDlChartDate(e.target.value, graphPeriod))}
                          className="sr-only"
                          tabIndex={-1}
                          aria-hidden
                        />
                        <input
                          ref={monthPickerRef}
                          type="month"
                          max={todayStr().slice(0, 7)}
                          value={(selectedDate || todayStr()).slice(0, 7)}
                          onChange={(e) => e.target.value && setSelectedDate(clampDlChartDate(`${e.target.value}-01`, graphPeriod))}
                          className="sr-only"
                          tabIndex={-1}
                          aria-hidden
                        />
                        <button type="button" onClick={() => changeDate(-1)} className={`${textSecondary} hover:opacity-80`}>◀</button>
                        {graphPeriod === 'Year' ? (
                          <select
                            value={(selectedDate || todayStr()).slice(0, 4)}
                            onChange={(e) => setSelectedDate(clampDlChartDate(`${e.target.value}-01-01`, 'Year'))}
                            className={`text-sm min-w-[4.5rem] rounded-md border px-2 py-1 ${isDark ? 'bg-[#1e293b] border-slate-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                          >
                            {Array.from({ length: Math.max(1, new Date().getFullYear() - 2019) }, (_, i) => 2020 + i)
                              .filter((y) => y <= new Date().getFullYear())
                              .map((y) => (
                                <option key={y} value={y}>{y}</option>
                              ))}
                          </select>
                        ) : (
                          <button
                            type="button"
                            onClick={openWebDlPeriodPicker}
                            className={`text-sm ${textPrimary} min-w-[100px] text-center`}
                          >
                            {graphDateDisplay}
                          </button>
                        )}
                        <button type="button" onClick={() => changeDate(1)} className={`${textSecondary} hover:opacity-80`}>▶</button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4 text-xs">
                  <span className="w-3 h-3 rounded-full bg-blue-500 shrink-0"></span>
                  <span className={textSecondary}>{t('totalProductionLabel')}</span>
                </div>
                <div className="flex-1 min-h-[280px] relative">
                  {chartLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-[#0f172a]/80 z-10 rounded-lg">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('loading')}</span>
                      </div>
                    </div>
                  )}
                  <ResponsiveContainer width="100%" height="100%">
                    {graphPeriod === 'Day' ? (
                      <AreaChart data={webGraphData} margin={{ top: 24, right: 20, left: 12, bottom: 28 }}>
                        <defs><linearGradient id="dlFillWeb" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22d3ee" stopOpacity={0.35} /><stop offset="100%" stopColor="#22d3ee" stopOpacity={0} /></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} vertical={false} />
                        <XAxis
                          dataKey={webChartDataKey}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                          interval={0}
                          ticks={['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:00']}
                          dy={8}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          width={52}
                          padding={{ top: 12, bottom: 12 }}
                          tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                          tickFormatter={(v) => chartPowerTickLabel(v, powerDisplayUnit)}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                            border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                            borderRadius: '8px',
                            color: isDark ? '#fff' : '#000',
                          }}
                          labelFormatter={(label) => (label ? `Koha: ${label}` : label)}
                          formatter={(v) => [chartPowerTickLabel(v, powerDisplayUnit), t('productionLabel')]}
                        />
                        <Area type="linear" dataKey="value" fill="url(#dlFillWeb)" stroke="none" />
                        <Line type="linear" dataKey="value" stroke="#22d3ee" strokeWidth={2} dot={false} />
                      </AreaChart>
                    ) : (
                      <BarChart data={webGraphData} margin={{ top: 24, right: 20, left: 12, bottom: 28 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} vertical={false} />
                        <XAxis
                          dataKey={webChartDataKey}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12, angle: -45, textAnchor: 'end' }}
                          interval="preserveStartEnd"
                          dy={8}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          width={52}
                          padding={{ top: 12, bottom: 12 }}
                          tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                          tickFormatter={(v) => chartPowerTickLabel(v, powerDisplayUnit)}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                            border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                            borderRadius: '8px',
                            color: isDark ? '#fff' : '#000',
                          }}
                          formatter={(v) => [chartPowerTickLabel(v, powerDisplayUnit), t('productionLabel')]}
                        />
                        <Bar dataKey="value" fill="#22d3ee" radius={[5, 5, 0, 0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DataLoggerDetail;
